import React, { useEffect, useRef } from "react";
import { GoogleMap } from "@/shared/components/common/GoogleMap";
import shareIcon from "@/assets/images/share.png";
import styles from "./HomePage.module.css";
import {
  useHomePageViewModal,
  type CommunityMapMarker,
} from "@/features/home/viewModal/homePageViewModal";
import { ServiceExpansionRequestModal } from "@/features/home/view/ServiceExpansionRequestModal";
import { Modal } from "@/shared/modal";
import { useAddress } from "@/shared/hooks";
import { useAddressListViewModel } from "@/shared/components/common/AddressSelectPill/AdrressListviewmodel";
import { PowerBillCard } from "@/features/powerBill/views/powerBillCard";
import { powerbillService } from "@/features/powerBill/services/powerBill.services";
import { propertyService } from "@/features/properties/model/propertyServices";
import { routes } from "@/app/navigation/routes";
const MapSection: React.FC<{
  address: string;
  center: { lat: number; lng: number };
  markers: CommunityMapMarker[];
}> = ({ address, center, markers }) => {
  return <GoogleMap address={address} center={center} zoom={12} markers={markers} />;
};

/**
 * HomePage
 * Responsive property overview dashboard (market values, tax, map, community safety)
 */
const DocCardDocumentIcon = () => (
  <svg
    className={styles.settlementWaiverIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 3h6l4 4v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v4h4M8 10h8M8 14h8M8 18h5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const DocCardInvoiceIcon = () => (
  <svg
    className={styles.settlementWaiverIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 3h6l4 4v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <text x="12" y="16.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="currentColor">
      $
    </text>
  </svg>
);

const DocCardEyeIcon = () => (
  <svg
    className={styles.settlementWaiverActionIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.5 12C4.5 7.5 8 5 12 5s7.5 2.5 9.5 7c-2 4.5-5.5 7-9.5 7s-7.5-2.5-9.5-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const DocCardDownloadIcon = () => (
  <svg
    className={styles.settlementWaiverActionIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 4v10M8.5 10.5 12 14l3.5-3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M5 18h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DocCardPayIcon = () => (
  <svg
    className={styles.settlementWaiverActionIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const SavingsPiggyIcon = () => (
  <svg
    className={styles.savingsPiggyIcon}
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <ellipse cx="32" cy="38" rx="22" ry="16" fill="#166534" />
    <circle cx="50" cy="34" r="7" fill="#166534" />
    <circle cx="53" cy="32" r="1.6" fill="#86EFAC" />
    <rect x="22" y="28" width="16" height="3" rx="1.5" fill="#0F3D24" />
    <ellipse cx="20" cy="52" rx="4" ry="3" fill="#14532D" />
    <ellipse cx="44" cy="52" rx="4" ry="3" fill="#14532D" />
    <circle cx="24" cy="36" r="2.2" fill="#BBF7D0" />
    <path
      d="M14 36c-3 0-5 2-5 4.5S11 45 14 45"
      stroke="#14532D"
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
    <circle cx="38" cy="18" r="7" fill="#EAB308" />
    <circle cx="38" cy="18" r="5.2" fill="#FACC15" />
    <text x="38" y="21" textAnchor="middle" fontSize="7" fontWeight="700" fill="#854D0E">
      $
    </text>
  </svg>
);

const SavingsCashIcon = () => (
  <svg
    className={styles.savingsCashIcon}
    viewBox="0 0 120 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="28" y="18" width="64" height="42" rx="6" fill="#16A34A" transform="rotate(-6 60 39)" />
    <rect x="30" y="28" width="64" height="42" rx="6" fill="#15803D" transform="rotate(-2 62 49)" />
    <rect x="32" y="38" width="64" height="42" rx="6" fill="#166534" />
    <rect x="48" y="38" width="32" height="42" fill="#F8FAFC" opacity="0.95" />
    <text x="64" y="64" textAnchor="middle" fontSize="16" fontWeight="700" fill="#166534">
      $
    </text>
    <circle cx="86" cy="72" r="22" fill="#EAB308" />
    <circle cx="86" cy="72" r="17" fill="#FACC15" />
    <circle cx="86" cy="72" r="12" fill="#FEF08A" />
    <text x="86" y="78" textAnchor="middle" fontSize="16" fontWeight="800" fill="#A16207">
      $
    </text>
    <path d="M18 22l2 5 5 1-4 3 1 5-4-3-4 3 1-5-4-3 5-1 2-5z" fill="#FACC15" />
    <path
      d="M102 18l1.5 3.5 3.5.7-2.7 2.2.7 3.5-3-2-3 2 .7-3.5-2.7-2.2 3.5-.7L102 18z"
      fill="#FDE047"
    />
    <circle cx="22" cy="48" r="2" fill="#86EFAC" opacity="0.7" />
    <circle cx="108" cy="42" r="1.5" fill="#86EFAC" opacity="0.6" />
  </svg>
);

const ProtestReviewedCheckIcon = () => (
  <svg
    className={styles.protestReviewedCheckIcon}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6.5 12.5 10 16l7.5-8"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProtestNextCalendarIcon = () => (
  <svg
    className={styles.protestNextIconSvg}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ProtestNextDocumentIcon = () => (
  <svg
    className={styles.protestNextIconSvg}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M8 3h6l4 4v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v4h4M8 10h8M8 14h8M8 18h5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ProtestNextRibbonIcon = () => (
  <svg
    className={styles.protestNextIconSvg}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="9" r="5" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M8.5 14 7 21l5-2.5L17 21l-1.5-7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const ProtestMountainIllustration = () => (
  <svg
    className={styles.protestMountainIllustration}
    viewBox="0 0 88 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M8 52 28 24l14 18 12-14 26 24H8Z" fill="#86EFAC" />
    <path d="M28 24 42 42 54 28 72 52H28V24Z" fill="#4ADE80" />
    <path d="M54 28 66 44 80 52H54V28Z" fill="#22C55E" />
    <path d="M54 20v32" stroke="#EAB308" strokeWidth="2" strokeLinecap="round" />
    <path d="M50 24h8l-4-6-4 6Z" fill="#FACC15" />
    <path
      d="M18 14 20 18l4 .8-3.2 2.6.8 4-3.6-2.8-3.6 2.8.8-4L14 18.8l4-.8 2-4.8Z"
      fill="#FDE047"
    />
    <path
      d="M70 10 71.5 13l3.2.6-2.6 2.1.6 3.2-2.8-2.2-2.8 2.2.6-3.2-2.6-2.1 3.2-.6L70 10Z"
      fill="#FDE047"
    />
  </svg>
);

const protestRejectNextSteps = [
  {
    key: "next-year",
    title: "Try Again Next Year",
    description: "We'll review your property and market updates to build a stronger case.",
    iconClass: styles.protestNextIconGreen,
    titleClass: styles.protestNextTitleGreen,
    Icon: ProtestNextCalendarIcon,
  },
  {
    key: "evidence",
    title: "Stronger Evidence, Stronger Case",
    description:
      "Submit more supporting documents like recent repairs, comparable sales, or appraisal reports to strengthen your protest.",
    iconClass: styles.protestNextIconBlue,
    titleClass: styles.protestNextTitleBlue,
    Icon: ProtestNextDocumentIcon,
  },
  {
    key: "support",
    title: "We're in Your Corner",
    description:
      "Our team will continue to monitor your property and fight for the savings you deserve.",
    iconClass: styles.protestNextIconPurple,
    titleClass: styles.protestNextTitlePurple,
    Icon: ProtestNextRibbonIcon,
  },
] as const;

export const HomePage: React.FC = () => {
  const vm = useHomePageViewModal();
  console.log("vm.journeyItems", vm.journeyItems);
  const { selectedAddress, setSelectedAddress } = useAddress();
  const { options: addressOptions } = useAddressListViewModel();
  const settlementWaiverPreviewRef = useRef<HTMLDivElement | null>(null);

  // const handleViewSettlementWaiverFullscreen = useCallback(() => {
  //   const previewElement = settlementWaiverPreviewRef.current;
  //   if (!previewElement) return;

  //   if (document.fullscreenElement) {
  //     void document.exitFullscreen();
  //     return;
  //   }

  //   if (previewElement.requestFullscreen) {
  //     void previewElement.requestFullscreen();
  //   }
  // }, []);

  useEffect(() => {
    // if (addressOptions.length === 0) {
    //   if (selectedAddress) {
    //     clearSelectedAddress();
    //   }
    //   return;
    // }

    // Keep user-picked property unless current selection is missing from available options.
    if (selectedAddress && addressOptions.some((option) => option.value === selectedAddress)) {
      return;
    }

    const first = addressOptions.at(0);
    if (!first) return;
    setSelectedAddress(first.value);
  }, [addressOptions, selectedAddress, setSelectedAddress]);

  useEffect(() => {
    if (!selectedAddress) return;
    const fetchPropertyDetails = async () => {
      const PropertyDetails = await propertyService.getPropertyById(selectedAddress);
      return PropertyDetails;
    };
    fetchPropertyDetails().then(async (PropertyDetails) => {
      console.log("PropertyDetails", PropertyDetails);
      if (PropertyDetails) {
        const response = await powerbillService.getEletricityEligible(
          PropertyDetails.address.zip,
          PropertyDetails.address.state,
          ""
        );
        console.log("response", response);
        if (response?.data?.is_eligible) {
          vm.setIsPowerbillEligible(true);
        } else {
          vm.setIsPowerbillEligible(false);
        }
      }
    });
  }, [selectedAddress, vm]);

  return (
    <div className={styles.homePage}>
      <div className={styles.layout}>
        {/* Top summary cards */}
        <div className={styles.summaryRow}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Market Value</div>
            <div className={styles.summaryValue}>
              {vm.isPropertyDetailsLoading ? "Loading…" : vm.formatCurrency(vm.marketValue)}
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryLabel}>Assessed Value</div>
            <div className={styles.summaryValue}>
              {vm.isPropertyDetailsLoading ? "Loading…" : vm.formatCurrency(vm.assessedValue)}
            </div>
          </div>
          <div className={`${styles.summaryCard} ${styles.summaryCardAccent}`}>
            <div className={styles.summaryLabel}>Total Tax Bill</div>
            <div className={`${styles.summaryValue} ${styles.summaryValueAccent}`}>
              {vm.isPropertyDetailsLoading ? "Loading…" : vm.formatCurrency(vm.totalTaxBill)}
            </div>
          </div>
        </div>

        {vm.propertyDetailsError && (
          <div style={{ marginTop: 10, color: "#b42318", fontSize: 14 }}>
            {(vm.propertyDetailsError as Error)?.message || "Failed to load property details."}
          </div>
        )}

        {/* Main content: left stack (savings + map), right stack (energy + safety) */}
        <div
          className={`${styles.mainRow} ${!vm.isPowerbillEligible ? styles.mainRowNoEnergy : ""}`}
        >
          <div className={styles.mainColumnLeft}>
            <div className={styles.mainRowSavings}>
              <div
                className={`${styles.savingsCard} ${styles.potentialSavingsCard}`}
                style={{
                  width: "100%",
                  borderRadius: "28px",
                  overflow: "hidden",
                  background: "linear-gradient(135deg, #F8FFF9 0%, #EEFDF3 100%)",
                  boxShadow: "0 12px 40px rgba(16, 185, 129, 0.12)",
                  border: "1px solid rgba(34,197,94,0.08)",
                }}
              >
                {/* LEFT IMAGE — full-height hero strip */}
                <div className={styles.potentialSavingsMedia} aria-hidden="true" />

                {/* RIGHT CONTENT */}
                <div
                  style={{
                    flex: 1,
                    padding: "22px 28px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    gap: "20px",
                  }}
                >
                  {vm.isProtestRejected ? (
                    <div className={styles.protestReviewedContent}>
                      <div className={styles.protestReviewedHeader}>
                        <div className={styles.protestReviewedTop}>
                          <div className={styles.protestReviewedIconWrap} aria-hidden="true">
                            <ProtestReviewedCheckIcon />
                          </div>
                          <div className={styles.protestReviewedTopText}>
                            <span className={styles.protestReviewedBadge}>Protest Reviewed</span>
                            <h3 className={styles.protestReviewedTitle}>Protest Reviewed</h3>
                            <p className={styles.protestReviewedMessage}>
                              Unfortunately, your property tax protest was not approved.
                            </p>
                          </div>
                        </div>
                        <div className={styles.protestReviewedDivider} />
                        <p className={styles.protestReviewedDetail}>
                          After a thorough review, we determined that your case did not meet the
                          criteria for a reduction this year.
                        </p>
                      </div>

                      <div className={styles.protestWhatsNext}>
                        <h4 className={styles.protestWhatsNextHeading}>What&apos;s Next?</h4>
                        <div className={styles.protestWhatsNextList}>
                          {protestRejectNextSteps.map((step) => (
                            <div key={step.key} className={styles.protestWhatsNextItem}>
                              <div
                                className={`${styles.protestNextIcon} ${step.iconClass}`}
                                aria-hidden="true"
                              >
                                <step.Icon />
                              </div>
                              <div className={styles.protestNextItemBody}>
                                <p className={`${styles.protestNextTitle} ${step.titleClass}`}>
                                  {step.title}
                                </p>
                                <p className={styles.protestNextDescription}>{step.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className={styles.protestReviewedFooter}>
                        <div className={styles.protestReviewedFooterText}>
                          <p className={styles.protestReviewedFooterTitle}>
                            Your persistence makes a difference!
                          </p>
                          <p className={styles.protestReviewedFooterSubtext}>
                            Thank you for trusting us. We&apos;ll keep working for you.
                          </p>
                        </div>
                        <ProtestMountainIllustration />
                      </div>
                    </div>
                  ) : (
                    <>
                      {vm.isProtestApproved && (
                        <div className={styles.protestApprovedHeader}>
                          <div className={styles.protestReviewedIconWrap} aria-hidden="true">
                            <ProtestReviewedCheckIcon />
                          </div>
                          <div className={styles.protestReviewedTopText}>
                            <h3 className={styles.protestReviewedTitle}>
                              Congratulations! Your case has been WON
                            </h3>
                            <p className={styles.protestReviewedMessage}>
                              We&apos;ve successfully reduced your property taxes.
                              {/* Don&apos;t worry, we&apos;ll help you. */}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* SAVINGS */}
                      <div className={styles.savingsShowcase}>
                        <div className={styles.savingsShowcaseHero}>
                          <div className={styles.savingsPiggyWrap} aria-hidden="true">
                            <SavingsPiggyIcon />
                          </div>

                          <div className={styles.savingsShowcaseCenter}>
                            <span className={styles.savingsEstimatedBadge}>Estimated Savings</span>
                            <h3 className={styles.savingsShowcaseTitle}>Potential Savings</h3>
                            <div className={styles.savingsShowcaseValue}>
                              {vm.isPropertyDetailsLoading
                                ? "Loading…"
                                : vm.formatCurrency(vm.amountSaved ?? 0)}
                            </div>
                            <p className={styles.savingsShowcaseCaption}>
                              Estimated Property Tax Reduction
                            </p>
                          </div>

                          <div className={styles.savingsCashWrap} aria-hidden="true">
                            <SavingsCashIcon />
                          </div>
                        </div>

                        <div className={styles.savingsShowcaseDivider} />

                        <p className={styles.savingsShowcaseSummary}>
                          Save up to{" "}
                          <span className={styles.savingsShowcaseSummaryAmount}>
                            {vm.isPropertyDetailsLoading
                              ? "…"
                              : vm.formatCurrency(vm.amountSaved ?? 0)}
                          </span>{" "}
                          through your property protest.
                        </p>
                      </div>
                      {/* JOURNEY */}
                      {!vm.showRefundInvoice &&
                        !vm.activeRefundInvoice &&
                        !vm.isProtestApproved && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "14px",
                            }}
                          >
                            {vm.journeyItems?.map((item, index) => (
                              <div
                                key={item.key}
                                onClick={item.onClick}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "18px",
                                  cursor: "pointer",
                                }}
                              >
                                {/* TIMELINE */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "52px",
                                      height: "52px",
                                      borderRadius: "50%",
                                      background: "#16A34A",
                                      color: "#fff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: 700,
                                      fontSize: "18px",
                                      boxShadow: "0 8px 20px rgba(34,197,94,0.25)",
                                    }}
                                  >
                                    {(item.key === "payment" && vm.isPaymentCompleted) ||
                                    (item.key === "supporting-documents" && vm.hasProtestFiled) ||
                                    (item.key === "homestead" && vm.homesteadFiled)
                                      ? "✓"
                                      : index + 1}
                                  </div>

                                  {index !== vm.journeyItems.length - 1 && (
                                    <div
                                      style={{
                                        width: "2px",
                                        flex: 1,
                                        minHeight: "50px",
                                        background: "#BBF7D0",
                                        marginTop: "6px",
                                      }}
                                    />
                                  )}
                                </div>

                                {/* STEP CARD */}
                                <div
                                  style={{
                                    flex: 1,
                                    background: "#FFFFFF",
                                    borderRadius: "18px 20px ",
                                    padding: "22px",
                                    border: "1px solid rgba(34,197,94,0.08)",
                                    boxShadow: "0 8px 24px rgba(16,185,129,0.08)",
                                    transition: "0.2s ease",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: "20px",
                                      width: "100%",
                                    }}
                                  >
                                    <div>
                                      <div
                                        // style={{
                                        //   fontWeight: 700,
                                        //   fontSize: "20px",
                                        //   color: "#111827",
                                        //   marginBottom: "8px",
                                        //   letterSpacing: "-0.2px",
                                        // }}
                                        style={{
                                          flex: 1,
                                          minWidth: 0,
                                        }}
                                      >
                                        {item.title}
                                      </div>

                                      <div
                                        style={{
                                          fontSize: "15px",
                                          color: "#6B7280",
                                          lineHeight: 1.6,
                                        }}
                                      >
                                        {item.subtitle}
                                      </div>
                                    </div>

                                    <div
                                      style={{
                                        width: "48px",
                                        height: "48px",
                                        minWidth: "48px",
                                        borderRadius: "50%",
                                        background: "rgba(34,197,94,0.12)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#16A34A",
                                        fontSize: "24px",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        transition: "0.2s ease",
                                      }}
                                    >
                                      ➜
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      {(vm.showSettlementWaiverDocument ||
                        (vm.showRefundInvoice && vm.activeRefundInvoice)) && (
                        <div className={styles.documentsPanel}>
                          {vm.showSettlementWaiverDocument && (
                            <div className={styles.settlementWaiverCard}>
                              <div className={styles.settlementWaiverIconWrap} aria-hidden="true">
                                <DocCardDocumentIcon />
                                <span className={styles.settlementWaiverIconBadgeSuccess}>✓</span>
                              </div>

                              <div className={styles.settlementWaiverContent}>
                                <div className={styles.settlementWaiverTitleRow}>
                                  <span className={styles.settlementWaiverBadge}>
                                    <span
                                      className={styles.settlementWaiverBadgeCheck}
                                      aria-hidden="true"
                                    >
                                      ✓
                                    </span>
                                    Available
                                  </span>
                                  <h3 className={styles.settlementWaiverTitle}>
                                    Settlement Waiver Document
                                  </h3>
                                </div>
                                <p className={styles.settlementWaiverSubtext}>
                                  View and download your settlement waiver document.
                                </p>
                              </div>

                              <div className={styles.settlementWaiverActions}>
                                <button
                                  type="button"
                                  className={styles.settlementWaiverActionButton}
                                  aria-label="View settlement waiver document"
                                  onClick={vm.handleViewSettlementWaiverDocument}
                                >
                                  <span className={styles.settlementWaiverActionIconBox}>
                                    <DocCardEyeIcon />
                                  </span>
                                  <span className={styles.settlementWaiverActionLabel}>View</span>
                                </button>
                                <button
                                  type="button"
                                  className={styles.settlementWaiverActionButton}
                                  aria-label="Download settlement waiver document"
                                  disabled={vm.isSettlementWaiverDownloading}
                                  onClick={() => void vm.handleDownloadSettlementWaiverDocument()}
                                >
                                  <span className={styles.settlementWaiverActionIconBox}>
                                    <DocCardDownloadIcon />
                                  </span>
                                  <span className={styles.settlementWaiverActionLabel}>
                                    {vm.isSettlementWaiverDownloading ? "…" : "Download"}
                                  </span>
                                </button>
                              </div>
                            </div>
                          )}

                          {vm.showRefundInvoice && vm.activeRefundInvoice && (
                            <div className={styles.settlementWaiverCard}>
                              <div className={styles.settlementWaiverIconWrap} aria-hidden="true">
                                <DocCardInvoiceIcon />
                                <span
                                  className={
                                    vm.isRefundInvoicePayable
                                      ? styles.settlementWaiverIconBadgeWarn
                                      : styles.settlementWaiverIconBadgeSuccess
                                  }
                                >
                                  {vm.isRefundInvoicePayable ? "!" : "✓"}
                                </span>
                              </div>

                              <div className={styles.settlementWaiverContent}>
                                <div className={styles.settlementWaiverTitleRow}>
                                  <span
                                    className={
                                      vm.isRefundInvoicePayable
                                        ? styles.settlementWaiverBadgeDue
                                        : styles.settlementWaiverBadge
                                    }
                                  >
                                    {!vm.isRefundInvoicePayable && (
                                      <span
                                        className={styles.settlementWaiverBadgeCheck}
                                        aria-hidden="true"
                                      >
                                        ✓
                                      </span>
                                    )}
                                    {vm.isRefundInvoicePayable ? "Payment due" : "Paid"}
                                  </span>
                                  <h3 className={styles.settlementWaiverTitle}>Refund Invoice</h3>
                                </div>
                                <p className={styles.settlementWaiverSubtext}>
                                  Invoice {vm.activeRefundInvoice.invoice_no}
                                  {vm.activeRefundInvoice.due_date
                                    ? ` • Due ${vm.activeRefundInvoice.due_date}`
                                    : ""}
                                </p>
                                <p className={styles.settlementWaiverSubtextSecondary}>
                                  {vm.isRefundInvoicePayable
                                    ? "Pay online using your refund invoice link."
                                    : "View your refund invoice."}
                                </p>
                              </div>

                              <div className={styles.settlementWaiverActions}>
                                {Boolean(vm.activeRefundInvoice.payment_link?.trim()) && (
                                  <button
                                    type="button"
                                    className={styles.settlementWaiverActionButton}
                                    aria-label={
                                      vm.isRefundInvoicePayable
                                        ? "Pay refund invoice"
                                        : "View refund invoice"
                                    }
                                    onClick={
                                      vm.isRefundInvoicePayable
                                        ? vm.handlePayRefundInvoice
                                        : vm.handleViewRefundInvoice
                                    }
                                  >
                                    <span className={styles.settlementWaiverActionIconBox}>
                                      {vm.isRefundInvoicePayable ? (
                                        <DocCardPayIcon />
                                      ) : (
                                        <DocCardEyeIcon />
                                      )}
                                    </span>
                                    <span className={styles.settlementWaiverActionLabel}>
                                      {vm.isRefundInvoicePayable ? "Pay Now" : "View Invoice"}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.mainRowMap}>
              <div className={styles.mapCard}>
                <div className={styles.mapHeader}>
                  <button
                    type="button"
                    className={styles.mapExpandButton}
                    onClick={() => vm.setIsMapModalOpen(true)}
                  >
                    Open Map
                  </button>
                </div>
                <div className={styles.mapBody}>
                  <MapSection
                    address={vm.effectiveAddress}
                    center={vm.mapCenter}
                    markers={vm.communityMarkers}
                  />
                  <div className={styles.mapFooter}>
                    <div className={styles.mapFooterLabel}>Community Data</div>
                    <div className={styles.mapFooterAddressRow}>
                      <span className={styles.mapFooterPin} aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 22s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <div className={styles.mapFooterAddress}>{vm.effectiveAddress}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.mainColumnRight}>
            {vm.isPowerbillEligible && (
              <div className={styles.mainRowEnergy}>
                <PowerBillCard
                  navigateTo={routes.powerCardPlans}
                  selectedProperty={selectedAddress}
                />
              </div>
            )}

            <aside className={styles.rightColumn}>
              <div className={styles.panelHeader}>
                <div className={styles.panelTitleWrapper}>
                  <span className={styles.panelIcon}>🛡️</span>
                  <span className={styles.panelTitle}>Community Safety</span>
                </div>
                <div className={styles.tabs}>
                  <button
                    type="button"
                    className={`${styles.tab} ${vm.activeTab === "crime" ? styles.tabActive : ""}`}
                    onClick={() => vm.setActiveTab("crime")}
                  >
                    {`Crime Report (${vm.crimeCount})`}
                  </button>
                  <button
                    type="button"
                    className={`${styles.tab} ${vm.activeTab === "offenders" ? styles.tabActive : ""}`}
                    onClick={() => vm.setActiveTab("offenders")}
                  >
                    {`Sex Offenders (${vm.offendersCount})`}
                  </button>
                </div>
              </div>
              <button
                type="button"
                className={`${styles.fileButton} ${styles.shareIconButton}`}
                disabled={vm.isSharingPropertyTaxData || vm.isPropertyDetailsLoading}
                aria-label="Share Property Tax Data"
                onClick={() => void vm.handleSharePropertyTaxData()}
              >
                <img src={shareIcon} alt="" aria-hidden="true" className={styles.shareIconImage} />
              </button>
              <div className={styles.recordsHeader}>
                <div className={styles.recordsTitle}>Recent Records</div>
                {/* <button type="button" className={styles.seeAll}>
                <span>See All</span>
                <span aria-hidden="true">›</span>
              </button> */}
              </div>

              {vm.isCommunityLoading && (
                <div style={{ padding: 12, fontSize: 14, color: "#667085" }}>
                  Loading community data…
                </div>
              )}
              {vm.communityError && (
                <div style={{ padding: 12, fontSize: 14, color: "#b42318" }}>
                  {vm.communityError.message || "Failed to load community data."}
                </div>
              )}

              <div
                ref={vm.recordsListRef}
                className={styles.recordsList}
                style={
                  vm.recordsListMaxHeight
                    ? { maxHeight: `${vm.recordsListMaxHeight}px` }
                    : undefined
                }
              >
                {!vm.isCommunityLoading && !vm.communityError && vm.records.length === 0 && (
                  <div style={{ padding: 12, fontSize: 14, color: "#667085" }}>
                    No records available.
                  </div>
                )}
                {!vm.isCommunityLoading &&
                  vm.records.map((record) => (
                    <div key={record.id} className={styles.recordItem}>
                      <div className={styles.avatar}>
                        {vm.activeTab === "offenders" ? (
                          record.imageUrl ? (
                            <img
                              src={record.imageUrl}
                              alt={record.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            record.name
                              .split(" ")
                              .map((part) => part.charAt(0))
                              .join("")
                              .toUpperCase()
                          )
                        ) : (
                          record.name
                            .split(" ")
                            .map((part) => part.charAt(0))
                            .join("")
                            .toUpperCase()
                        )}
                      </div>
                      <div className={styles.recordContent}>
                        <div className={styles.recordName}>{record.name}</div>
                        <div className={styles.recordMeta}>
                          {vm.activeTab === "crime"
                            ? record.gender
                            : `${record.gender}${record.age ? ` · Age ${record.age}` : ""}`}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
              {vm.hasRecordOverflow && (
                <div className={styles.scrollHint}>Scroll to see more records</div>
              )}
            </aside>
          </div>
        </div>

        <ServiceExpansionRequestModal
          isOpen={vm.isServiceExpansionModalOpen}
          onClose={vm.closeServiceExpansionModal}
          defaults={vm.serviceExpansionDefaults}
        />

        <Modal
          isOpen={vm.isMapModalOpen}
          onClose={() => vm.setIsMapModalOpen(false)}
          unconstrained
          className={styles.mapModalContent}
          headerClassName={styles.mapModalHeader}
          title={
            <>
              <div className={styles.mapTitle}>Community Data</div>
              <div className={styles.mapAddress}>{vm.effectiveAddress}</div>
            </>
          }
          titleClassName={styles.mapModalTitleGroup}
          bodyClassName={styles.mapModalBody}
          ariaLabel="Community data map"
        >
          <GoogleMap
            address={vm.effectiveAddress}
            center={vm.mapCenter}
            zoom={13}
            markers={vm.communityMarkers}
          />
        </Modal>

        <Modal
          isOpen={vm.isSettlementWaiverViewerOpen}
          onClose={vm.closeSettlementWaiverViewer}
          unconstrained
          className={styles.settlementWaiverModalContent}
          headerClassName={styles.settlementWaiverModalHeader}
          title={
            <div className={styles.settlementWaiverModalTitleBlock}>
              <div className={styles.settlementWaiverModalTitleIconWrap}>
                <DocCardDocumentIcon />
              </div>
              <div className={styles.settlementWaiverModalTitleContent}>
                <div className={styles.settlementWaiverModalTitleRow}>
                  <span className={styles.settlementWaiverModalTitleText}>
                    Settlement Waiver Document
                  </span>
                  <span className={styles.settlementWaiverModalBadge}>Available</span>
                </div>
                <p className={styles.settlementWaiverModalSubtitle}>
                  View and download your settlement waiver document.
                </p>
              </div>
            </div>
          }
          titleClassName={styles.settlementWaiverModalTitle}
          bodyClassName={styles.settlementWaiverModalBody}
          ariaLabel="Settlement waiver document preview"
        >
          <div className={styles.settlementWaiverModalBodyInner}>
            {/* <div className={styles.settlementWaiverInfoBanner}>
              <div className={styles.settlementWaiverInfoBannerIconWrap}>
                <SettlementWaiverShieldIcon />
              </div>
              <div className={styles.settlementWaiverInfoBannerContent}>
                <p className={styles.settlementWaiverInfoBannerTitle}>
                  This document is ready for your review.
                </p>
                <p className={styles.settlementWaiverInfoBannerText}>
                  You can view it online or download a copy for your records.
                </p>
              </div>
            </div> */}

            <div ref={settlementWaiverPreviewRef} className={styles.settlementWaiverPreviewShell}>
              <div className={styles.settlementWaiverPreviewInner}>
                {vm.settlementWaiverDocumentUrl && (
                  <iframe
                    src={vm.settlementWaiverDocumentUrl}
                    title="Settlement waiver document"
                    className={styles.settlementWaiverPreviewFrame}
                  />
                )}
              </div>
            </div>

            {/* <div className={styles.settlementWaiverModalFooter}>
              <button
                type="button"
                className={styles.settlementWaiverFullscreenButton}
                onClick={handleViewSettlementWaiverFullscreen}
              >
                <SettlementWaiverEyeIcon />
                View Full Screen
              </button>
              <button
                type="button"
                className={styles.settlementWaiverDownloadButton}
                disabled={vm.isSettlementWaiverDownloading}
                onClick={() => void vm.handleDownloadSettlementWaiverDocument()}
              >
                <SettlementWaiverDownloadIcon />
                Download PDF
              </button>
            </div> */}
          </div>
        </Modal>
      </div>
    </div>
  );
};
